/** Exact decimal addition for the group subtotals - CLAUDE.md rule 5: never `float`.
 *
 * Quantities arrive as decimal strings (`"184000.0000000000"`). They are summed as scaled integers
 * (`BigInt`), so `0.1 + 0.2` is `0.3` here, and the result is a decimal string for the caller's
 * formatter. Anything that is not a plain decimal is **skipped and counted** by the caller, never read
 * as zero: a subtotal that silently dropped a row would be wrong in the quiet direction.
 */

const DECIMAL = /^(-)?(\d+)(?:\.(\d+))?$/;

export function isDecimal(value: string): boolean {
  return DECIMAL.test(value.trim());
}

/** The sum of the decimal strings, or `null` for an empty list. Throws on a string that is not a
 * decimal: callers filter with `isDecimal` first. */
export function sumDecimals(values: readonly string[]): string | null {
  if (values.length === 0) return null;
  const parsed = values.map((raw) => {
    const match = DECIMAL.exec(raw.trim());
    if (!match) throw new Error(`not a decimal: ${raw}`);
    return { negative: match[1] === "-", whole: match[2] as string, fraction: match[3] ?? "" };
  });
  const scale = Math.max(...parsed.map((p) => p.fraction.length));
  let total = BigInt(0);
  for (const p of parsed) {
    const scaled = BigInt(p.whole + p.fraction.padEnd(scale, "0"));
    total += p.negative ? -scaled : scaled;
  }
  const negative = total < BigInt(0);
  const digits = (negative ? -total : total).toString().padStart(scale + 1, "0");
  const whole = digits.slice(0, digits.length - scale);
  const fraction = scale === 0 ? "" : digits.slice(digits.length - scale);
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}
